import { useState } from 'react';
import { defineStepper } from '@stepperize/react';
import { ArrowLeftIcon, ArrowRightIcon, CalendarClockIcon, InfoIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { apiErrorMessage } from '@/shared/api-client';
import { Avatar, AvatarFallback } from '@/shared/ui/components/avatar';
import { Button } from '@/shared/ui/components/button';
import { Card, CardContent } from '@/shared/ui/components/card';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/components/select';
import { cn } from '@/shared/lib/utils';
import { DEFAULT_SLOT_GRANULARITY_MINUTES } from '@/modules/venue/slotGranularity';
import { OperatingHoursPicker } from './OperatingHoursPicker';
import {
  emptyEditableWeekdayHours,
  snapEditableWeekdayHours,
  toRequestWeekdayHours,
  validateEditableWeekdayHours,
  type EditableWeekdayHours,
} from './operatingHoursForm';
import { useCountryOptions, useCreateVenue, type CountryOption } from './venues';

const SLOT_GRANULARITY_CHOICES = [30, 60];

const { useStepper } = defineStepper([{ id: 'basic' }, { id: 'schedule' }]);

const STEP_ICON = { basic: InfoIcon, schedule: CalendarClockIcon };

/**
 * Creating a venue is a two-step page rather than a dialog: the seven weekday rows plus the
 * granularity choice never fit a modal, and splitting them off the name/country pair keeps each
 * screen to one decision. Slot granularity lives with the hours because it decides which times
 * the hour pickers may offer at all.
 */
export function CreateVenuePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuthorization();
  const stepper = useStepper();
  const countryOptions = useCountryOptions(true);
  const createVenue = useCreateVenue();

  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [slotGranularityMinutes, setSlotGranularityMinutes] = useState<number>(
    DEFAULT_SLOT_GRANULARITY_MINUTES,
  );
  const [operatingHours, setOperatingHours] =
    useState<EditableWeekdayHours[]>(emptyEditableWeekdayHours);
  const [error, setError] = useState<string | null>(null);

  if (!auth.can('venue.manage')) return <Forbidden />;

  async function submit() {
    setError(null);

    const validationError = validateEditableWeekdayHours(operatingHours, slotGranularityMinutes);
    if (validationError) {
      setError(t(`venues.operatingHours.errors.${validationError}`));
      return;
    }

    try {
      await createVenue.mutateAsync({
        name,
        countryCode,
        slotGranularityMinutes,
        operatingHours: operatingHours.map(toRequestWeekdayHours),
      });
      toast.success(t('venues.toast.created'));
      await navigate('/admin/venues');
    } catch (caught) {
      setError(apiErrorMessage(t, 'venues', caught));
    }
  }

  return (
    <section className="grid gap-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('venues.create.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('venues.create.description')}</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/admin/venues">{t('venues.create.back')}</Link>
        </Button>
      </div>

      <Card className="gap-0 p-0 md:grid md:max-lg:grid-cols-5 lg:grid-cols-4">
        <CardContent className="col-span-5 p-6 max-md:border-b md:border-r md:max-lg:col-span-2 lg:col-span-1">
          <nav aria-label={t('venues.create.steps.label')}>
            <ol className="flex flex-col justify-between gap-x-2 gap-y-4">
              {stepper.steps.map((step, index) => {
                const Icon = STEP_ICON[step.id];
                return (
                  <li key={step.id}>
                    <Button
                      variant="ghost"
                      className="h-auto w-full shrink-0 justify-start gap-2 rounded bg-transparent! p-0"
                      onClick={() => void stepper.goTo(step.id)}
                    >
                      <Avatar className="size-9.5">
                        <AvatarFallback
                          className={cn('text-foreground', {
                            'bg-primary text-primary-foreground shadow-sm': index <= stepper.index,
                          })}
                        >
                          <Icon className="size-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col items-start">
                        <span>{t(`venues.create.steps.${step.id}.title`)}</span>
                        <span className="text-muted-foreground text-sm">
                          {t(`venues.create.steps.${step.id}.description`)}
                        </span>
                      </div>
                    </Button>
                  </li>
                );
              })}
            </ol>
          </nav>
        </CardContent>

        {stepper.match({
          basic: () => (
            <BasicDataStep
              name={name}
              onName={setName}
              countryCode={countryCode}
              onCountryCode={setCountryCode}
              countries={countryOptions.data ?? []}
              countriesLoading={countryOptions.isLoading}
              countriesError={countryOptions.isError}
              onNext={() => void stepper.next()}
            />
          ),
          schedule: () => (
            <ScheduleStep
              slotGranularityMinutes={slotGranularityMinutes}
              onSlotGranularity={setSlotGranularityMinutes}
              hours={operatingHours}
              onHours={setOperatingHours}
              error={error}
              submitting={createVenue.isPending}
              onPrev={() => void stepper.prev()}
              onNext={() => void submit()}
            />
          ),
        })}
      </Card>
    </section>
  );
}

/** The right-hand pane of the card: step content on top, Previous/Next pinned underneath. */
function StepPane({
  children,
  onPrev,
  onNext,
  nextLabel,
  nextDisabled,
  error,
}: {
  children: React.ReactNode;
  onPrev?: () => void;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
  error?: string | null;
}) {
  const { t } = useTranslation('common');

  return (
    <CardContent className="col-span-5 flex flex-col gap-6 p-6 md:col-span-3">
      {children}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-between gap-4">
        <Button type="button" variant="secondary" size="lg" onClick={onPrev} disabled={!onPrev}>
          <ArrowLeftIcon />
          {t('venues.create.previous')}
        </Button>
        <Button type="button" size="lg" onClick={onNext} disabled={nextDisabled}>
          {nextLabel}
          <ArrowRightIcon />
        </Button>
      </div>
    </CardContent>
  );
}

function BasicDataStep({
  name,
  onName,
  countryCode,
  onCountryCode,
  countries,
  countriesLoading,
  countriesError,
  onNext,
}: {
  name: string;
  onName: (value: string) => void;
  countryCode: string;
  onCountryCode: (value: string) => void;
  countries: CountryOption[];
  countriesLoading: boolean;
  countriesError: boolean;
  onNext: () => void;
}) {
  const { t } = useTranslation('common');
  const selected = countries.find((option) => option.countryCode === countryCode);

  return (
    <StepPane
      onNext={onNext}
      nextLabel={t('venues.create.next')}
      nextDisabled={!name.trim() || !countryCode}
    >
      <div>
        <h2 className="text-lg font-semibold">{t('venues.create.steps.basic.title')}</h2>
        <p className="text-muted-foreground text-sm">{t('venues.create.basic.description')}</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className="flex flex-col items-start gap-1">
          <Label htmlFor="venue-name">{t('venues.fields.name')}</Label>
          <Input id="venue-name" value={name} onChange={(event) => onName(event.target.value)} />
          <p className="text-muted-foreground/80 text-xs">{t('venues.create.basic.nameHint')}</p>
        </div>

        <div className="flex flex-col items-start gap-1">
          <Label htmlFor="venue-country">{t('venues.fields.country')}</Label>
          <Select value={countryCode} onValueChange={onCountryCode}>
            <SelectTrigger id="venue-country" className="w-full">
              <SelectValue
                placeholder={
                  countriesLoading
                    ? t('venues.reference.loading')
                    : t('venues.create.basic.countryPlaceholder')
                }
              />
            </SelectTrigger>
            <SelectContent>
              {countries.map((option) => (
                <SelectItem key={option.countryCode} value={option.countryCode}>
                  {option.countryCode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p
            className={cn(
              'text-xs',
              countriesError ? 'text-destructive' : 'text-muted-foreground/80',
            )}
          >
            {countriesError
              ? t('venues.reference.loadFailed')
              : t('venues.create.basic.countryHint')}
          </p>
        </div>
      </div>

      {!countriesLoading && !countriesError && countries.length === 0 && (
        <p className="text-sm text-destructive">{t('venues.reference.noCountries')}</p>
      )}

      <div className="grid gap-2 rounded-lg border bg-muted/40 p-4 text-sm">
        <p className="font-medium">{t('venues.create.snapshotPreview')}</p>
        {selected ? (
          <dl className="grid grid-cols-3 gap-2">
            <div>
              <dt className="text-muted-foreground text-xs">{t('venues.fields.language')}</dt>
              <dd>{selected.language}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">{t('venues.fields.currency')}</dt>
              <dd>{selected.currency}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">{t('venues.fields.timezone')}</dt>
              <dd>{selected.timezone}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-muted-foreground">{t('venues.create.basic.snapshotEmpty')}</p>
        )}
      </div>
    </StepPane>
  );
}

function ScheduleStep({
  slotGranularityMinutes,
  onSlotGranularity,
  hours,
  onHours,
  error,
  submitting,
  onPrev,
  onNext,
}: {
  slotGranularityMinutes: number;
  onSlotGranularity: (minutes: number) => void;
  hours: EditableWeekdayHours[];
  onHours: (next: EditableWeekdayHours[]) => void;
  error: string | null;
  submitting: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation('common');

  function changeGranularity(minutes: number) {
    onSlotGranularity(minutes);
    onHours(snapEditableWeekdayHours(hours, minutes));
  }

  return (
    <StepPane
      onPrev={onPrev}
      onNext={onNext}
      nextLabel={submitting ? t('venues.create.submitting') : t('venues.create.submit')}
      nextDisabled={submitting}
      error={error}
    >
      <div>
        <h2 className="text-lg font-semibold">{t('venues.create.steps.schedule.title')}</h2>
        <p className="text-muted-foreground text-sm">{t('venues.create.schedule.description')}</p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="venue-slot-granularity">{t('venues.fields.slotGranularity')}</Label>
        <div id="venue-slot-granularity" className="inline-flex w-fit rounded-md border p-1">
          {SLOT_GRANULARITY_CHOICES.map((minutes) => (
            <button
              key={minutes}
              type="button"
              aria-pressed={slotGranularityMinutes === minutes}
              onClick={() => changeGranularity(minutes)}
              className={cn(
                'rounded px-4 py-1.5 text-sm',
                slotGranularityMinutes === minutes
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {t('venues.create.schedule.granularityMinutes', { minutes })}
            </button>
          ))}
        </div>
      </div>

      <OperatingHoursPicker
        slotGranularityMinutes={slotGranularityMinutes}
        hours={hours}
        onHours={onHours}
      />
    </StepPane>
  );
}
